<template>
  <el-form-item :class="{ desktop: isDesktop }" label="Targeting">
    <el-collapse v-model="activePanels">
      <el-collapse-item title="Spots & Domains" name="traffic_sources">
        <el-form-item label="Spots filter">
          <RonTypeSelector
            v-model="campaign.ron_type"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item v-if="showSpotsSelector" :error="errors.spot_ids">
          <template #label>
            <span>Spots</span>
            <el-tooltip
              content="Enter spot IDs separated by comma or one per line. You can also attach a saved list below — spots from both sources will be combined."
              placement="top"
              :show-after="300"
            >
              <el-icon class="label-tooltip-icon"><QuestionFilled /></el-icon>
            </el-tooltip>
          </template>
          <SpotsSelector
            v-model="campaign.spot_ids"
            :stream="modelValue.stream"
            :type-id="modelValue.type_id"
            :disabled="false"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item v-if="showSpotsSelector" :error="errors.spots_list_ids">
          <template #label>
            <span>Saved Spots Lists</span>
            <el-tooltip
              content="Attach a reusable list of spots. Changes to the list automatically apply to all campaigns using it."
              placement="top"
              :show-after="300"
            >
              <el-icon class="label-tooltip-icon"><QuestionFilled /></el-icon>
            </el-tooltip>
          </template>
          <WhiteListsSelector
            v-model="campaign.spots_list_ids"
            :items="spotsLists"
            title="Select a saved list..."
            @update:modelValue="update"
          />
        </el-form-item>
        <hr />
        <el-form-item
          :error="errors.blacklist_domains || errors.whitelist_domains"
        >
          <template #label>
            <span>Domain filter</span>
            <el-tooltip
              content="Filter traffic by the website domain where your ad is shown. Works together with Spots, IP and ISP filters (all conditions must match)."
              placement="top"
              :show-after="300"
            >
              <el-icon class="label-tooltip-icon"><QuestionFilled /></el-icon>
            </el-tooltip>
          </template>
          <WhiteBlackLists
            v-model="campaign.use_whitelist_domains"
            title="Domains"
            type="domains"
            hint="One domain per line, e.g.: example.com (without https:// and www)"
            :black-list="campaign.blacklist_domains"
            :white-list="campaign.whitelist_domains"
            @update="update"
          />
        </el-form-item>
        <hr />
        <el-form-item
          v-if="isStaff"
          :error="errors.keywords"
          label="Keywords filter (admin only)"
        >
          <KeywordsInput
            v-model="campaign.keywords"
            class="full-width-control"
            @update:modelValue="update"
          />
        </el-form-item>
      </el-collapse-item>
      <el-collapse-item title="Geo, IP & ISP" name="geo">
        <CountrySelector
          v-model="campaign.country_codes"
          @update:modelValue="update"
        />
        <el-checkbox v-model="addAreaTargeting">
          Add specific geographic location targeting
        </el-checkbox>
        <el-form-item v-if="addAreaTargeting">
          <el-radio v-model="target" value="cities"> City targeting </el-radio>
          <el-radio v-model="target" value="regions">
            Region targeting
          </el-radio>
          <RegionSelector
            v-if="target === 'regions'"
            v-model="campaign.region_ids"
            :target="target"
            :country-iso="campaign.country_codes"
            @update:modelValue="update"
          />
          <RegionSelector
            v-else
            v-model="campaign.city_ids"
            :target="target"
            :country-iso="campaign.country_codes"
            @update:modelValue="update"
          />
        </el-form-item>
        <hr />
        <el-form-item :error="errors.isp" class="isp-types">
          <template #label>
            <span>ISP filter</span>
            <el-tooltip
              content="Filter traffic by Internet Service Provider name. Useful for targeting or blocking specific mobile carriers."
              placement="top"
              :show-after="300"
            >
              <el-icon class="label-tooltip-icon"><QuestionFilled /></el-icon>
            </el-tooltip>
          </template>
          <IspEditor
            :model-value="campaign.isp"
            :isp-mode="campaign.isp_mode"
            :country-codes="campaign.country_codes"
            :connection-type-ids="campaign.connection_type_ids"
            @update:modelValue="update"
          />
        </el-form-item>
        <hr />
        <el-form-item
          :error="errors.ip_nets || errors.ip_string"
          label="IP filter"
          class="ip-range-types"
        >
          <WhiteBlackLists
            v-model="campaign.use_whitelist_ip"
            title="IPs"
            type="ip"
            hint="Supports human-readable ip ranges (1.1.1.1-2.2.2.2) or with subnet mask (1.1.1.1/24, 2001:4860:4860::8890/124). One per line."
            :black-list="campaign.ips"
            :white-list="campaign.ips"
            @update="update"
          />
        </el-form-item>
        <el-form-item :error="errors.ip_list_ids">
          <template #label>
            <span>Saved IP Lists</span>
            <el-tooltip
              content="Attach a reusable IP list. The same list can be used as 'Only listed' or 'Exclude listed' in different campaigns. Changes apply automatically."
              placement="top"
              :show-after="300"
            >
              <el-icon class="label-tooltip-icon"><QuestionFilled /></el-icon>
            </el-tooltip>
          </template>
          <WhiteListsSelector
            v-model="campaign.ip_list_ids"
            class="third-width-control"
            :items="ipsLists"
            title="Select a saved list..."
            @update:modelValue="update"
          />
        </el-form-item>
        <hr />
        <template v-if="reTargetingAllowed">
          <el-form-item
            :error="errors.retargeting_list_ids"
            label="Retargeting"
          >
            <WhiteListsSelector
              v-model="campaign.retargeting_list_ids"
              class="third-width-control"
              :items="retargetingLists"
              title="Please choose retargeting lists"
              @update:modelValue="update"
            />
          </el-form-item>
          <hr />
        </template>
        <DeviceLanguageSelect
          v-model="campaign.language_ids"
          class="third-width-control"
          @update:modelValue="update"
        />
      </el-collapse-item>
      <el-collapse-item title="Device" name="device">
        <DeviceSelector
          v-model="campaign.device_ids"
          class="third-width-control"
          @update:modelValue="update"
        />
        <ConnectionTypeSelector
          v-model="campaign.connection_type_ids"
          class="third-width-control"
          @update:modelValue="update"
        />
        <el-form-item label-width="150px" label="Platforms">
          <PlatformsSelector
            v-model="platforms"
            class="third-width-control"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item label-width="150px" label="Browsers">
          <BrowsersSelector
            v-model="browsers"
            class="third-width-control"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item v-if="isStaff" label="User-agent filter (admin only)">
          <WhiteBlackLists
            v-model="campaign.use_whitelist_user_agents"
            title="User-agents"
            type="user-agents"
            hint="Allow/Disallow traffic if user agents contains one of the substrings. One substring per line."
            :black-list="campaign.blacklist_user_agents"
            :white-list="campaign.whitelist_user_agents"
            @update="update"
          />
        </el-form-item>
      </el-collapse-item>
      <el-collapse-item title="Limits" name="limits">
        <el-form-item
          :class="{ desktop: isDesktop }"
          :error="errors.start_at || errors.finish_at"
          label="Date range"
        >
          <DateRangeSelector v-model="range" @update:modelValue="update" />
        </el-form-item>
        <CampaignLimits v-model="campaign.limits" @update:modelValue="update" />
        <el-form-item class="mt-1" label="Time zone is UTC-0">
          <WeekdaysSelect
            v-model="campaign.weekdays"
            class="w-20 mr-1"
            @update:modelValue="update"
          />
          <TimeslotsSelect
            v-model="campaign.hours"
            class="w-20"
            @update:modelValue="update"
          />
        </el-form-item>
      </el-collapse-item>
      <el-collapse-item
        v-if="isStaff"
        title="Campaign limit settings (admin only)"
        name="campaign_limit_settings"
      >
        <CampaignLimitsSettings
          v-model="campaign"
          @update:modelValue="updateLimitSettings"
        />
      </el-collapse-item>
      <el-collapse-item
        v-if="testTargetingAllowed"
        title="Test Campaign settings"
        name="test_campaign_settings"
      >
        <TestCampaignLimits
          v-model="campaign.limits"
          @update:modelValue="update"
        />
      </el-collapse-item>
    </el-collapse>
  </el-form-item>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import CountrySelector from '@/apps/traforama/components/CountrySelector.vue'
import RegionSelector from '@/apps/traforama/components/RegionSelector.vue'
import IspEditor from '@/components/IspEditor.vue'
import DeviceLanguageSelect from '@/components/Select/DeviceLanguageSelect.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import ConnectionTypeSelector from '@/apps/traforama/components/ConnectionTypeSelector.vue'
import PlatformsSelector from '@/components/PlatformsSelector.vue'
import BrowsersSelector from '@/components/BrowsersSelector.vue'
import DateRangeSelector from '@/apps/traforama/components/DateRangeSelector.vue'
import WhiteBlackLists from '@/apps/traforama/components/WhiteBlackLists.vue'
import CampaignLimits from './CampaignLimits.vue'
import TestCampaignLimits from './TestCampaignLimits.vue'
import WeekdaysSelect from '@/components/Select/WeekdaysSelect.vue'
import RonTypeSelector from './RonTypeSelector.vue'
import SpotsSelector from '@/apps/traforama/components/SpotsSelector.vue'
import KeywordsInput from './KeywordsInput.vue'
import CampaignLimitsSettings from './CampaignLimitsSettings.vue'
import TimeslotsSelect from '@/components/Select/TimeslotsSelect.vue'
import WhiteListsSelector from '@/apps/traforama/components/WhiteListsSelector.vue'
import useResize from '@/hooks/useResize'
import { Option } from '@/api/regions'
import { RonType, CampaignValidationErrorsKey, IspMode } from '@/api/types'
import { useUserStore } from '@/store/modules/user'
import useValidation from '@/hooks/useValidation'
import { useIPListsStore } from '@/apps/traforama/store/ipLists'
import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'
import { useRetargetingListsStore } from '@/apps/traforama/store/retargetingLists'

import { FeatureFlags } from '@/apps/traforama/api/types'

export default defineComponent({
  name: 'CampaignTargeting',
  components: {
    CampaignLimitsSettings,
    CountrySelector,
    RegionSelector,
    DeviceLanguageSelect,
    IspEditor,
    DeviceSelector,
    ConnectionTypeSelector,
    PlatformsSelector,
    BrowsersSelector,
    DateRangeSelector,
    CampaignLimits,
    TestCampaignLimits,
    RonTypeSelector,
    SpotsSelector,
    WeekdaysSelect,
    TimeslotsSelect,
    KeywordsInput,
    WhiteListsSelector,
    WhiteBlackLists,
    QuestionFilled
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const spotsListsStore = useSpotsListsStore()
    const ipListsStore = useIPListsStore()
    const retargetingListsStore = useRetargetingListsStore()

    const userStore = useUserStore()

    const { isStaff } = userStore
    const campaign = ref()

    const addAreaTargeting = ref(
      props.modelValue.city_ids.length > 0 ||
        props.modelValue.region_ids.length > 0
    )
    const target = ref(
      props.modelValue.city_ids.length
        ? 'cities'
        : props.modelValue.region_ids.length
          ? 'regions'
          : 'cities'
    )

    campaign.value = { ...props.modelValue }

    const showSpotsSelector = computed(() => {
      return campaign.value.ron_type !== RonType.All
    })

    const spotsLists = computed(() => {
      return Object.values(spotsListsStore.currentItems)
    })

    const retargetingLists = computed(() => {
      return Object.values(retargetingListsStore.currentItems)
    })

    const ipsLists = computed(() => {
      return Object.values(ipListsStore.currentItems)
    })

    const activePanels = reactive<Array<string>>([])

    if (campaign.value.domainsChanged) {
      activePanels.push('traffic_sources')
    }

    const range = computed({
      get: () => {
        return [campaign.value.start_at, campaign.value.finish_at]
      },
      set: (val) => {
        if (Array.isArray(val) && val.length === 2) {
          campaign.value.start_at = val[0]
          campaign.value.finish_at = val[1]
        } else {
          campaign.value.start_at = ''
          campaign.value.finish_at = ''
        }
      }
    })

    const { isDesktop } = useResize()

    const platforms = computed({
      get: () => {
        return {
          // platform_id may be a number if it came from API
          platforms: campaign.value.platform_ids
            ? campaign.value.platform_ids.map((id: number | string) =>
                id.toString()
              )
            : [],
          specific: campaign.value.specific_platforms || []
        }
      },
      set: (val) => {
        campaign.value.platform_ids = val.platforms
        campaign.value.specific_platforms = val.specific
      }
    })

    function clearAreaTargeting() {
      campaign.value.city_ids = []
      campaign.value.region_ids = []
      update({})
    }

    watch(target, (_) => clearAreaTargeting())
    watch(addAreaTargeting, (_) => clearAreaTargeting())

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        campaign.value = val
        if (val.domainsChanged) activePanels.push('traffic_sources')
      }
    )

    const browsers = computed({
      get: () => {
        return {
          // platform_id may be a number if it came from API
          browsers: campaign.value.browser_ids
            ? campaign.value.browser_ids.map((id: number | string) =>
                id.toString()
              )
            : [],
          specific: campaign.value.specific_browsers || []
        }
      },
      set: (val) => {
        campaign.value.browser_ids = val.browsers
        campaign.value.specific_browsers = val.specific
      }
    })

    const testTargetingAllowed = computed(() => {
      return userStore.testTargetingAllowed
    })

    const reTargetingAllowed = computed(() =>
      userStore.featureEnabled(FeatureFlags.Retargeting)
    )

    const update = ({
      useWhiteList,
      whiteList,
      blackList,
      type,
      isp,
      ispMode
    }: {
      useWhiteList?: boolean
      whiteList?: string[]
      blackList?: string[]
      type?: string
      isp?: string
      ispMode?: IspMode
    }) => {
      const valueToUpdate = {
        hours: campaign.value.hours,
        weekdays: campaign.value.weekdays,
        limits: campaign.value.limits,
        connection_type_ids: campaign.value.connection_type_ids,
        device_ids: campaign.value.device_ids,
        language_ids: campaign.value.language_ids,
        ips: campaign.value.ips,
        country_codes: campaign.value.country_codes,
        city_ids: campaign.value.city_ids,
        region_ids: campaign.value.region_ids,
        use_whitelist_domains: campaign.value.use_whitelist_domains,
        blacklist_domains: campaign.value.blacklist_domains,
        whitelist_domains: campaign.value.whitelist_domains,
        use_whitelist_user_agents: campaign.value.use_whitelist_user_agents,
        blacklist_user_agents: campaign.value.blacklist_user_agents,
        whitelist_user_agents: campaign.value.whitelist_user_agents,
        spot_ids: campaign.value.spot_ids,
        ron_type: campaign.value.ron_type,
        browser_ids: campaign.value.browser_ids,
        specific_browsers: campaign.value.specific_browsers,
        platform_ids: campaign.value.platform_ids,
        specific_platforms: campaign.value.specific_platforms,
        start_at: campaign.value.start_at,
        finish_at: campaign.value.finish_at,
        ip_range_type: campaign.value.ip_range_type,
        use_whitelist_ip: campaign.value.use_whitelist_ip,
        keywords: campaign.value.keywords,
        spots_list_ids: campaign.value.spots_list_ids,
        ip_list_ids: campaign.value.ip_list_ids,
        limits_slow_stop: campaign.value.limits_slow_stop,
        limits_rps: campaign.value.limits_rps,
        slow_stop_factor: campaign.value.slow_stop_factor,
        rps_factor: campaign.value.rps_factor,
        retargeting_list_ids: campaign.value.retargeting_list_ids
      }

      // Handle IspEditor data
      if (typeof isp !== 'undefined') {
        valueToUpdate.isp = isp
      }
      if (typeof ispMode !== 'undefined') {
        campaign.value.isp_mode = ispMode
        valueToUpdate.isp_mode = ispMode
      }

      if (typeof useWhiteList !== 'undefined') {
        if (type === 'domains') {
          valueToUpdate.use_whitelist_domains = useWhiteList
          valueToUpdate.blacklist_domains = blackList
          valueToUpdate.whitelist_domains = whiteList
        } else if (type === 'ip') {
          valueToUpdate.use_whitelist_ip = useWhiteList
          valueToUpdate.ips = useWhiteList ? whiteList : blackList
        } else {
          valueToUpdate.use_whitelist_user_agents = useWhiteList
          valueToUpdate.blacklist_user_agents = blackList
          valueToUpdate.whitelist_user_agents = whiteList
        }
      }
      context.emit('update', valueToUpdate)
    }

    const updateLimitSettings = (val: object) => {
      campaign.value = { ...campaign.value, ...val }
      update({})
    }

    const { validationErrors, scrollToFirstError } = useValidation(
      CampaignValidationErrorsKey
    )
    const errors = validationErrors()

    watch(
      errors,
      ({
        clicks_total,
        clicks_hourly,
        clicks_daily,
        budget_hourly,
        budget_daily,
        budget_total
      }) => {
        if (
          clicks_total ||
          clicks_hourly ||
          clicks_daily ||
          budget_hourly ||
          budget_daily ||
          budget_total
        ) {
          activePanels.push('limits')
          scrollToFirstError()
        }
      }
    )

    return {
      target,
      addAreaTargeting,
      showSpotsSelector,
      activePanels,
      platforms,
      browsers,
      range,
      isDesktop,
      campaign,
      update,
      updateLimitSettings,
      testTargetingAllowed,
      isStaff,
      errors,
      spotsLists,
      ipsLists,
      retargetingLists,
      reTargetingAllowed
    }
  }
})
</script>

<style lang="scss" scoped>
.desktop .el-collapse {
  margin-left: 2em;
}

.el-form-item__content > div {
  vertical-align: top;
}

:deep(.isp-types .el-form-item__content) {
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

:deep(.isp-types) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.desktop :deep(.third-width-control .el-select),
.desktop :deep(.third-width-control.el-select) {
  width: 33%;
  max-width: 33%;
}

.narrow-select {
  max-width: 320px;
  width: 100%;

  :deep(.el-select) {
    width: 100%;
  }
}

.label-tooltip-icon {
  margin-left: 4px;
  color: var(--el-text-color-secondary);
  cursor: help;
  vertical-align: middle;
}
</style>

<style lang="scss">
.desktop .el-collapse {
  .el-collapse-item__content {
    .el-form-item__content {
      margin-left: 0 !important;
    }
    .el-form-item__label {
      width: inherit !important;
    }
  }
}
.ip-range-types {
  margin-bottom: 0 !important;
  margin-top: 26px !important;
}
.ip-ranges-input {
  margin-top: 0 !important;
}
</style>
